/**
 * Review and sign the US tax form (S-31): every line pre-filled from the profile and read-only, the
 * IRS's own declaration, and a signature that is the LAST thing on the screen.
 *
 * What the person is never asked: anything the profile already holds. Wrong data is corrected in the
 * profile, not typed over the form. What they may add: a mailing address when it differs (line 4) and
 * a U.S. tax number when the profile has none. The declaration is shown in English, as the IRS prints
 * it (`translate="no"`): the Spanish screen around it is a convenience, the words signed are these.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CountryPicker from '../CountryPicker';
import taxFormsService, {
  type SignedTaxForm,
  type TaxFormPreview,
} from '../../services/taxForms.service';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import ExecutiveRefusal from './ExecutiveRefusal';
import { CALL_CONTEXT } from '../../lib/executiveRefusal';
import {
  CORRECTION_PATH,
  SIGN_TITLE,
  canSign,
  lineRows,
  missingThings,
} from '../../lib/taxForm';
import { calendarDate } from './enteringFund.helpers';
import { activeLanguage } from '../../i18n/language';
import { colors } from '../../styles/theme';

interface Props {
  /** The series the client is about to start, when they come from one. */
  readonly productId: string | null;
  readonly onSigned: (signed: SignedTaxForm) => void;
  readonly onCancel: () => void;
}

const FIELD = {
  background: colors.canvas,
  // The edge of a field: 3:1 against the surface (WCAG 1.4.11, #362).
  border: `1.5px solid ${colors.controlBorder}`,
  color: colors.ink,
} as const;

function Box({ children, tone = 'info' }: Readonly<{ children: React.ReactNode; tone?: 'info' | 'warn' }>): JSX.Element {
  const warn = tone === 'warn';
  return (
    <div
      className="rounded-lg p-3 mb-3 text-sm"
      role={warn ? 'alert' : undefined}
      style={{
        background: warn ? colors.warningBg : colors.infoBg,
        border: `1px solid ${warn ? '#FCD34D' : colors.infoBorder}`,
        color: colors.ink,
      }}
    >
      {children}
    </div>
  );
}

export function LinesTable({ preview }: Readonly<{ preview: TaxFormPreview }>): JSX.Element {
  const rows = lineRows(preview.form ?? 'w8ben', preview.lines ?? {});
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-3 gap-x-6 mb-4" translate="no">
      {rows.map((row) => (
        <div key={`${row.line}-${row.label}`} className="sm:col-span-3 grid sm:grid-cols-3 gap-x-6">
          <dt className="text-xs" style={{ color: colors.muted }}>
            {row.line ? `${row.line}. ` : ''}
            {row.label}
          </dt>
          <dd className="sm:col-span-2 text-sm font-mono" style={{ color: colors.ink }}>
            {row.value || '—'}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function Declaration({ preview }: Readonly<{ preview: TaxFormPreview }>): JSX.Element | null {
  const cert = preview.certification;
  if (!cert) return null;
  return (
    <section
      className="rounded-lg p-4 mb-4"
      style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}
      translate="no"
    >
      <h4 className="text-sm font-semibold mb-2" style={{ color: colors.ink }}>
        {cert.heading}
      </h4>
      <p className="text-xs mb-2" style={{ color: colors.inkSoft, lineHeight: 1.55 }}>
        {cert.intro}
      </p>
      <ol className="text-xs mb-2 pl-5 list-decimal" style={{ color: colors.inkSoft, lineHeight: 1.55 }}>
        {cert.items.map((item) => (
          <li key={item} className="mb-1">
            {item}
          </li>
        ))}
      </ol>
      <p className="text-xs" style={{ color: colors.inkSoft, lineHeight: 1.55 }}>
        {cert.closing}
      </p>
    </section>
  );
}

export default function TaxFormSigning({ productId, onSigned, onCancel }: Readonly<Props>): JSX.Element {
  const [preview, setPreview] = useState<TaxFormPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [signError, setSignError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [usTin, setUsTin] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mailingOpen, setMailingOpen] = useState(false);
  const [mailingStreet, setMailingStreet] = useState('');
  const [mailingCity, setMailingCity] = useState('');
  const [mailingCountry, setMailingCountry] = useState<string | null>(null);
  const [backupNotified, setBackupNotified] = useState(false);
  // One key for one intent to sign: a double click or a retry after a dropped connection is the same
  // signing; a changed form is a new one.
  const key = useRef(crypto.randomUUID());

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const loaded = await taxFormsService.current(productId);
      // What the person typed on their previous form comes back filled in: a renewal asks nothing twice.
      const carried = loaded.carried;
      if (carried) {
        setUsTin(carried.us_tin);
        setMailingOpen(!!carried.mailing_street);
        setMailingStreet(carried.mailing_street);
        setMailingCity(carried.mailing_city);
        setMailingCountry(carried.mailing_country || null);
      }
      setPreview(loaded);
    } catch (e: unknown) {
      setLoadError(getApiErrorMessage(e, "We couldn't prepare your tax form. Please try again shortly."));
    }
  }, [productId]);

  useEffect(() => { void load(); }, [load]);

  const edited = (set: (v: string) => void) => (value: string): void => {
    key.current = crypto.randomUUID();
    set(value);
  };

  async function sign(): Promise<void> {
    if (!preview?.certification || busy) return;
    setBusy(true);
    setSignError(null);
    try {
      const done = await taxFormsService.sign(
        {
          signerName: signerName.trim(),
          language: activeLanguage(),
          productId,
          usTin: preview.needs_us_tin ? usTin : null,
          backupWithholdingNotified: preview.form === 'w9' && backupNotified,
          linesSha256: preview.lines_sha256 ?? '',
          mailingStreet: mailingOpen ? mailingStreet : '',
          mailingCity: mailingOpen ? mailingCity : '',
          mailingCountry: mailingOpen ? (mailingCountry ?? '') : '',
          mfaCode: preview.step_up_required ? mfaCode : null,
          certificationVersion: preview.certification.version,
        },
        key.current,
      );
      onSigned(done);
    } catch (e: unknown) {
      const code = getApiErrorCode(e);
      setSignError(getApiErrorMessage(e, "We couldn't sign your tax form. Please try again."));
      if (code === 'certification_changed' || code === 'tax_form_incomplete') await load();
      key.current = crypto.randomUUID();
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <Box tone="warn">{loadError}</Box>;
  if (!preview) return <p className="text-sm" style={{ color: colors.muted }}>Preparing your tax form…</p>;
  if (!preview.applies || !preview.form) {
    return (
      <>
        <Box>You do not need a U.S. tax form right now.</Box>
        <button type="button" className="text-sm font-semibold" onClick={onCancel}>Close</button>
      </>
    );
  }

  const form = preview.form;
  const contradicts = (preview.contradicts ?? []).length > 0;
  const missing = preview.missing ?? [];
  const ready = canSign(preview, {
    signerName, confirmed, usTin, mfaCode, mailingOpen, mailingStreet, mailingCountry: mailingCountry ?? '',
  });

  return (
    <div>
      <h3 className="text-base font-semibold mb-1" style={{ color: colors.ink }}>
        {SIGN_TITLE[form]}
      </h3>
      <p className="text-xs mb-3" style={{ color: colors.muted }} translate="no">
        Official IRS form, {preview.revision}
      </p>

      <Box>
        Everything below comes from your profile, so you do not type it again. If something is
        wrong, ask us to correct it first.{' '}
        <Link to={CORRECTION_PATH} style={{ color: colors.ink, fontWeight: 600 }}>
          Ask us to correct my information →
        </Link>
      </Box>

      {preview.rfc_message && <Box tone="warn">{preview.rfc_message}</Box>}

      {contradicts && (
        <ExecutiveRefusal
          message="We can't complete this form online. Talk to your Account Executive."
          productId={productId}
          context={CALL_CONTEXT.taxForm}
        />
      )}
      {missing.length > 0 && (
        <Box tone="warn">
          <p>Your profile is missing what the form needs. Ask us to add it, then come back.</p>
          <ul className="list-disc pl-5 mt-1">
            {missingThings(missing).map((thing) => (
              <li key={thing}>{thing}</li>
            ))}
          </ul>
        </Box>
      )}

      <LinesTable preview={preview} />

      {!contradicts && form === 'w8ben' && (
        <div className="mb-4">
          <label className="flex items-start gap-2 text-sm" style={{ color: colors.ink }}>
            <input
              type="checkbox"
              checked={mailingOpen}
              onChange={(e) => { key.current = crypto.randomUUID(); setMailingOpen(e.target.checked); }}
              style={{ marginTop: 3 }}
            />
            {' '}
            My mailing address is different from the address above (line 4)
          </label>
          {mailingOpen && (
            <div className="grid gap-2 mt-2 max-w-md">
              <input
                aria-label="Mailing address, street"
                placeholder="Street and number"
                value={mailingStreet}
                onChange={(e) => edited(setMailingStreet)(e.target.value)}
                className="rounded-lg px-3 py-2 text-sm"
                style={FIELD}
              />
              <input
                aria-label="Mailing address, city and region"
                placeholder="City, state or province, postal code"
                value={mailingCity}
                onChange={(e) => edited(setMailingCity)(e.target.value)}
                className="rounded-lg px-3 py-2 text-sm"
                style={FIELD}
              />
              <CountryPicker
                id="tax-form-mailing-country"
                value={mailingCountry}
                onChange={(code) => { key.current = crypto.randomUUID(); setMailingCountry(code); }}
              />
            </div>
          )}
        </div>
      )}

      {!contradicts && preview.needs_us_tin && (
        <div className="mb-4 max-w-md">
          <label htmlFor="tax-form-us-tin" className="block text-xs mb-1" style={{ color: colors.muted }}>
            {preview.us_tin_required
              ? 'Your U.S. tax number (SSN or ITIN) — 9 digits'
              : 'Your U.S. tax number (SSN or ITIN), only if you have one — 9 digits'}
          </label>
          <input
            id="tax-form-us-tin"
            inputMode="numeric"
            autoComplete="off"
            maxLength={11}
            value={usTin}
            onChange={(e) => edited(setUsTin)(e.target.value)}
            className="rounded-lg px-3 py-2 text-sm font-mono w-full"
            style={FIELD}
          />
        </div>
      )}

      <p className="text-xs mb-4" style={{ color: colors.muted }}>
        Lines 7 and 10 stay blank: a special treaty rate, if one applies, is completed with your tax
        professional.
      </p>

      {!contradicts && missing.length === 0 && (
        <>
          {form === 'w9' && (
            <label className="flex items-start gap-2 text-sm mb-3" style={{ color: colors.ink }}>
              <input
                type="checkbox"
                checked={backupNotified}
                onChange={(e) => { key.current = crypto.randomUUID(); setBackupNotified(e.target.checked); }}
                style={{ marginTop: 3 }}
              />
              {' '}
              The IRS has notified me that I am currently subject to backup withholding
              (item 2 of the declaration is then crossed out).
            </label>
          )}

          <Declaration preview={preview} />

          {/* The signature is the last entry: nothing below it is read or asked. */}
          <div className="grid gap-3 max-w-md mb-3">
            <label className="flex items-start gap-2 text-sm" style={{ color: colors.ink }}>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => { key.current = crypto.randomUUID(); setConfirmed(e.target.checked); }}
                style={{ marginTop: 3 }}
              />
              <span>
                I have read the declaration above and make it under penalties of perjury.
                {preview.certification?.capacity && (
                  <span translate="no"> {preview.certification.capacity}</span>
                )}
              </span>
            </label>
            {preview.step_up_required && (
              <div>
                <label htmlFor="tax-form-code" className="block text-xs mb-1" style={{ color: colors.muted }}>
                  Code from your authenticator app
                </label>
                <input
                  id="tax-form-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => edited(setMfaCode)(e.target.value.replace(/\D/g, ''))}
                  className="rounded-lg px-3 py-2 text-sm font-mono w-40"
                  style={FIELD}
                />
              </div>
            )}
            <div>
              <label htmlFor="tax-form-signer" className="block text-xs mb-1" style={{ color: colors.muted }}>
                To sign, type your full legal name exactly as it appears on line 1
              </label>
              <input
                id="tax-form-signer"
                autoComplete="off"
                aria-describedby="tax-form-signer-hint"
                value={signerName}
                onChange={(e) => edited(setSignerName)(e.target.value)}
                className="rounded-lg px-3 py-2 text-sm w-full"
                style={FIELD}
              />
              <p id="tax-form-signer-hint" className="text-xs mt-1" style={{ color: colors.muted }}>
                Your name, the date and time, and a statement that you signed electronically are
                recorded with the form.
              </p>
            </div>
          </div>

          {signError && <Box tone="warn">{signError}</Box>}
        </>
      )}

      <div className="flex gap-3 items-center">
        {!contradicts && missing.length === 0 && (
          <button
            type="button"
            onClick={() => void sign()}
            disabled={!ready || busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{
              background: ready && !busy ? colors.gold : colors.disabledBg,
              color: colors.navy,
              cursor: ready && !busy ? 'pointer' : 'not-allowed',
            }}
          >
            {busy ? 'Signing…' : 'Sign electronically'}
          </button>
        )}
        <button type="button" onClick={onCancel} className="text-sm" style={{ color: colors.muted }}>
          Cancel
        </button>
      </div>
      <p className="sr-only" aria-live="polite">{signError ?? ''}</p>
    </div>
  );
}

/** What a client reads right after signing. */
export function signedMessage(signed: SignedTaxForm): string {
  return signed.valid_through
    ? `Signed. Your form is valid through ${calendarDate(signed.valid_through)}.`
    : 'Signed.';
}
